import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Topic } from "@src/pages/challenge-landing/topic-companies-details/types";

interface GetTopicByIdParams {
  categoryId: string;
  topicId: string;
  pageNo: number;
  pageSize: number;
  userId?: string;
}

export type APIResponse = {
  httpStatus: string;
  statusCode: number;
};

export interface GetAllCategoryTopics extends APIResponse {
  response: { id: string; name: string; description: string }[];
}

const BASE_URL: string = import.meta.env.VITE_TEST_FRESHERS_CHALLENGE_BASE_URL;

export const CategoryTopicApiSlice = createApi({
  reducerPath: "categoryTopicApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  tagTypes: ["getCategoryTopic"],
  endpoints: (builder) => ({
    getCategoryTopic: builder.query<
      any,
      { challengeCategoryId: string; pageNo: number; pageSize: number }
    >({
      query: ({ challengeCategoryId, pageNo, pageSize }) => ({
        url: `challenge-topic/get-category-challengeTopic?challengeCategoryId=${challengeCategoryId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
      providesTags: ["getCategoryTopic"],
    }),
    getTopicsForQuestion: builder.query<any, { challengeId: number }>({
      query: ({ challengeId }) => ({
        url: `challenge/topics-count?challengeId=${challengeId}`,
        method: "GET",
      }),
    }),
    getUnassignedTopicsForQuestion: builder.query<
      any,
      { challengeId: number | string; categoryId: string }
    >({
      query: ({ challengeId, categoryId }) => ({
        url: `challenge/get-all-challengeTopic-unmapped?challengeId=${challengeId}&categoryId=${categoryId}`,
        method: "GET",
      }),
    }),
    addTopicsForQuestion: builder.mutation<
      any,
      { challengeId: number; challengeTopicIds: string[] }
    >({
      query: ({ challengeId, challengeTopicIds }) => ({
        url: `challenge/assign-topics-challenge?challengeId=${challengeId}`,
        method: "PUT",
        body: challengeTopicIds,
      }),
    }),
    addCategoryTopic: builder.mutation<
      APIResponse,
      {
        challengeCategoryId: string;
        payload: { name: string; description: string };
      }
    >({
      query: ({ payload, challengeCategoryId }) => ({
        url: `challenge-topic`,
        method: "POST",
        body: payload,
        params: { challengeCategoryId: challengeCategoryId },
      }),
    }),
    getAllCategoryTopics: builder.query<
      GetAllCategoryTopics,
      { challengeCategoryId: string }
    >({
      query: ({ challengeCategoryId }) => ({
        url: `challenge-topic/get-all-category-challengeTopic`,
        method: "GET",
        params: { challengeCategoryId: challengeCategoryId },
      }),
    }),
    getCategoryTopicDropdown: builder.query<
      any,
      { challengeCategoryId: string }
    >({
      query: ({ challengeCategoryId }) => ({
        url: `challenge-topic/get-all-category-challengeTopic?challengeCategoryId=${challengeCategoryId}`,
        method: "GET",
      }),
    }),
    getAssignedQuestionsForTopic: builder.query<
      any,
      {
        challengeTopicId: string;
      }
    >({
      query: ({ challengeTopicId }) => ({
        url: `challenge-topic/get-all-challenges-by-topic?challengeTopicId=${challengeTopicId}`,
        method: "GET",
      }),
    }),
    getUnassignedQuestionsForTopic: builder.query<
      any,
      {
        challengeTopicId: string | number;
        categoryId: string;
      }
    >({
      query: ({ challengeTopicId, categoryId }) => ({
        url: `challenge-topic/get-all-challenges-unmapped-by-topic?challengeTopicId=${challengeTopicId}&categoryId=${categoryId}`,
        method: "GET",
      }),
    }),
    assignQuestionsForTopic: builder.mutation<
      any,
      {
        challengeTopicId: string | number;
        challengeIds: (string | number)[];
      }
    >({
      query: ({ challengeTopicId, challengeIds }) => ({
        url: `challenge-topic/assign-challenges-topic?challengeTopicId=${challengeTopicId}`,
        method: "PUT",
        body: challengeIds,
      }),
    }),
    getTopicById: builder.query<
      {
        httpStatus: string;
        statusCode: number;
        response: Topic;
      },
      GetTopicByIdParams
    >({
      query: ({ topicId, categoryId, pageNo, pageSize, userId }) => {
        const params: GetTopicByIdParams = {
          categoryId,
          topicId,
          pageNo,
          pageSize,
        };

        if (userId) {
          params.userId = userId;
        }

        return {
          url: `challenge-topic/get-by-id`,
          method: "GET",
          params,
        };
      },
    }),
  }),
});

export const {
  useGetCategoryTopicQuery,
  useLazyGetCategoryTopicQuery,
  useAddCategoryTopicMutation,
  useGetCategoryTopicDropdownQuery,
  useGetTopicsForQuestionQuery,
  useAddTopicsForQuestionMutation,
  useGetUnassignedTopicsForQuestionQuery,
  useLazyGetAllCategoryTopicsQuery,
  useGetUnassignedQuestionsForTopicQuery,
  useGetAssignedQuestionsForTopicQuery,
  useLazyGetAssignedQuestionsForTopicQuery,
  useAssignQuestionsForTopicMutation,
  useLazyGetTopicByIdQuery,
} = CategoryTopicApiSlice;
